import { type FC } from 'react';
import { useFragment, graphql } from 'react-relay';
import classnames from 'classnames';

import { useCurrency } from 'dibs-buyer-layout/exports/useCurrency.tsx';
import { Link } from 'dibs-elements/exports/Link.tsx';
import ArrowRight from 'dibs-icons/exports/legacy/ArrowRight.ts';
import { BuyerProductTile, TILE_SIZES } from 'dibs-buyer-product-tile/exports/BuyerProductTile.ts';

import { SharedHeart } from '../../sharedComponents/SharedHeart.tsx';
import { useHomePageContext } from '../HomePageContext.tsx';
import {
    trackCategoryClick,
    trackItemImpression,
    trackItemClick,
} from '../helpers/newArrivalsTracking.ts';

import dibsCss from 'dibs-css';

import { type HpSharedNewArrivalsModule_viewer$key } from './__generated__/HpSharedNewArrivalsModule_viewer.graphql.ts';
import { type HpSharedNewArrivalsModule_personalizationModule$key } from './__generated__/HpSharedNewArrivalsModule_personalizationModule.graphql.ts';

const viewerFragment = graphql`
    fragment HpSharedNewArrivalsModule_viewer on Viewer {
        ...BuyerProductTile_viewer
    }
`;

const personalizationModuleFragment = graphql`
    fragment HpSharedNewArrivalsModule_personalizationModule on PersonalizationModule {
        linkData {
            path
        }
        categoryTitle
        categoryItems {
            ...newArrivalsTracking_item
            ...SharedHeart_item
            ...BuyerProductTile_item
                @arguments(isTrade: $isTrade, priceBookName: $priceBookName, showSeller: false)
        }
        ...newArrivalsTracking_personalizationModule
    }
`;

type Props = {
    viewer: HpSharedNewArrivalsModule_viewer$key;
    personalizationModule: HpSharedNewArrivalsModule_personalizationModule$key | null;
    moduleIndex: number;
    favorites: $TSFixMe;
};

const HpSharedNewArrivalsModule: FC<Props> = ({
    viewer: viewerRef,
    personalizationModule: personalizationModuleRef,
    moduleIndex,
    favorites,
}) => {
    const { currency } = useCurrency();
    const { isMobile } = useHomePageContext();

    const viewer = useFragment(viewerFragment, viewerRef);
    const personalizationModule = useFragment(
        personalizationModuleFragment,
        personalizationModuleRef
    );

    const { linkData, categoryTitle, categoryItems } = personalizationModule || {};
    const moduleItems = categoryItems || [];
    const itemsPerModule = moduleItems.length;

    return (
        <div
            className={classnames(dibsCss.flex, dibsCss.flexCol, dibsCss.bgWhite, dibsCss.wFull, {
                [dibsCss.pSmall]: !isMobile,
                [dibsCss.pXsmall]: isMobile,
            })}
        >
            <div
                className={classnames(
                    dibsCss.sassyFontHeaderMedium,
                    dibsCss.mXsmall,
                    dibsCss.mbMedium
                )}
            >
                <Link
                    href={linkData?.path || ''}
                    underline="none"
                    onClick={() => {
                        trackCategoryClick({
                            personalizationModule: personalizationModule || null,
                        });
                    }}
                >
                    {categoryTitle}
                    <ArrowRight
                        className={classnames(dibsCss.h14px, dibsCss.w14px, dibsCss.mlSmaller)}
                    />
                </Link>
            </div>
            <div
                className={classnames(dibsCss.flex, dibsCss.mxXsmall, {
                    [dibsCss.gapSmall]: !isMobile,
                    [dibsCss.gapSmaller]: isMobile,
                })}
            >
                {moduleItems.map((item, index) => {
                    const itemIndex = moduleIndex * itemsPerModule + index;

                    return (
                        <BuyerProductTile
                            key={index}
                            viewer={viewer}
                            itemSearch={null}
                            item={item}
                            user={null}
                            afterDisplay
                            currency={currency}
                            imageBackgroundColor="white"
                            index={index}
                            itemsPerRow={2}
                            onClick={() =>
                                trackItemClick({
                                    item,
                                    itemIndex,
                                })
                            }
                            onContentVisible={() => trackItemImpression({ item, itemIndex })}
                            size={TILE_SIZES.small}
                            showMeasurements={false}
                            showMskuSwatch={false}
                            showQuickViewButton={false}
                            showPrice
                            showShippingCost
                            favoritesComponent={
                                <SharedHeart
                                    item={item}
                                    isMobile={isMobile}
                                    trackingList="homepage - new arrivals"
                                    favorites={favorites}
                                />
                            }
                        />
                    );
                })}
            </div>
        </div>
    );
};

export default HpSharedNewArrivalsModule;
